import {
  Icon,
  OptionsMenu,
  OptionsMenuItem,
  OptionsMenuTrigger,
  Panel,
  TooltipTabstop
} from '@deque/cauldron-react';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { type TransInterpolation } from '~/common/types';

import { type ProductSlugs } from '@deque/billing-utils';
import analyticsInstances, {
  getFallbackAnalyticsInstanceId
} from '~/common/analyticsInstances';
import { type SupportedIntegrationProductSlugs } from '~/common/constants';
import DeleteTemplateAlert from './DeleteTemplateAlert';
import styles from './TemplateCard.css';

export interface TemplateCardProps {
  templateId: string;
  templateName: string;
  project: string;
  issueType: string;
  updatedBy: string | null;
  updatedAt: string | null;
  instanceUrl: string;
  canEdit: boolean;
  integrationProductSlug:
    | SupportedIntegrationProductSlugs
    | ProductSlugs.axeAuditorIntegration;
  connectionId: string;
  token: string;
  enterpriseId: string;
  refreshTemplates: () => void;
}

function TemplateCard({
  templateId,
  templateName,
  project,
  issueType,
  updatedBy,
  updatedAt,
  instanceUrl,
  canEdit,
  integrationProductSlug,
  connectionId,
  token,
  enterpriseId,
  refreshTemplates
}: TemplateCardProps) {
  const { t } = useTranslation();
  const history = useHistory();
  const analytics =
    analyticsInstances[getFallbackAnalyticsInstanceId(integrationProductSlug)];

  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const isEditingDisabled = !project || !issueType;

  const onEditTemplateSelect = () => {
    if (isEditingDisabled) {
      return;
    }

    analytics.integrationTemplateEditClick({
      integration: integrationProductSlug,
      project,
      issueType,
      template: templateName,
      connectionId: connectionId,
      enterpriseId
    });
    history.push(
      `/configuration/integrations/${connectionId}/templates/${templateId}`
    );
  };

  return (
    <>
      <Panel className={styles.templateCard}>
        <div className={styles.templateCardContent}>
          <div>
            <h3>{templateName}</h3>
            <p>
              {project}: {issueType}
            </p>
            <p>{instanceUrl}</p>
          </div>
          <p className={styles.updateInfo}>
            {updatedAt && updatedBy && (
              <Trans>
                <span>{{ updatedBy } as TransInterpolation}</span> edited{' '}
                {{ updatedAt } as TransInterpolation}
              </Trans>
            )}
          </p>
        </div>
        {canEdit && (
          <div className={styles.actions}>
            <OptionsMenu
              className={styles.optionMenu}
              align="right"
              trigger={triggerProps => (
                <OptionsMenuTrigger {...triggerProps}>
                  <Icon
                    type="kabob"
                    label={t('Options for {{ name }} template', {
                      name: templateName
                    })}
                  />
                </OptionsMenuTrigger>
              )}
            >
              {/* TODO: Add back in post-MVP
           <OptionsMenuItem
            onSelect={() => location('/configuration/integrations')}
          >
            {t('Preview template')}
          </OptionsMenuItem> */}
              {isEditingDisabled ? (
                <TooltipTabstop
                  tooltip={t(
                    'You cannot edit this template because project or issue type was deleted.'
                  )}
                >
                  <OptionsMenuItem
                    disabled={isEditingDisabled}
                    onSelect={onEditTemplateSelect}
                  >
                    {t('Edit template')}
                  </OptionsMenuItem>
                </TooltipTabstop>
              ) : (
                <OptionsMenuItem
                  disabled={isEditingDisabled}
                  onSelect={onEditTemplateSelect}
                >
                  {t('Edit template')}
                </OptionsMenuItem>
              )}
              <OptionsMenuItem onSelect={() => setShowConfirmDelete(true)}>
                {t('Delete template')}
              </OptionsMenuItem>
            </OptionsMenu>
          </div>
        )}
      </Panel>
      {showConfirmDelete && (
        <DeleteTemplateAlert
          templateId={templateId}
          setShowConfirmDelete={setShowConfirmDelete}
          integrationProductSlug={integrationProductSlug}
          issueType={issueType}
          project={project}
          templateName={templateName}
          connectionId={connectionId}
          token={token}
          enterpriseId={enterpriseId}
          refreshTemplates={refreshTemplates}
        />
      )}
    </>
  );
}

export default TemplateCard;
